import { IFilterService } from "angular";

import { IGoal, IGoalNode, IGoalsIdMap, IGoalsAlignmentMeta, IPartialGoal, IGoalsAlignmentMatchedFiltersData, IGoalsAlignmentFilterMeta } from "./models";
import { AccountResolverService } from "../state/accountResolverService";
import { IPartialMetric, IMetric } from "@gtmhub/okrs/metrics";
import { IGoalsMetricsData } from "@gtmhub/sockets";
import { toIdMap, IdMap } from "@gtmhub/util";
import { ProgressColoring } from "@gtmhub/configuration";
import { IGridSortingRule, setPersistedScreenSettings, resetPersistedScreenSettings } from "@gtmhub/shared/components/grid-filtering-bar";
import { alphanumericComparator, sortingRulesToOrderStrings, getPersistedExpandedGoals, expandedGoalsKey } from "@gtmhub/okrs/alignment-tree";
import { expandedGoalsScreenPath } from "./filter-utils";

export class GoalsTreeService {
  public static $inject = ["$filter", "AccountResolverService", "ProgressColoring"];

  constructor(private $filter: IFilterService, private accountResolverService: AccountResolverService, private progressColoring: ProgressColoring) {}

  public updateGoalsTree(data: IGoalsMetricsData, goals: IGoal[]): void {
    if (data.accountId !== this.accountResolverService.getAccountId()) {
      return;
    }

    this.updateGoals(data.goals, goals);
    this.updateMetrics(data.metrics, goals);
  }

  public expandSet(sessionId: string, goalIds: string[]): void {
    const expandedGoals = getPersistedExpandedGoals(sessionId);
    for (const goalId of goalIds) {
      expandedGoals[goalId] = true;
    }
    this.setPersistedExpandedGoals(sessionId, expandedGoals);
  }

  public collapseSet(sessionId: string, goalIds: string[]): void {
    const expandedGoals = getPersistedExpandedGoals(sessionId);
    // expanded goals are shared between alignment and nested and may contain goal ids from other sessions
    for (const goalId of goalIds) {
      delete expandedGoals[goalId];
    }

    if (Object.keys(expandedGoals).length) {
      this.setPersistedExpandedGoals(sessionId, expandedGoals);
    } else {
      resetPersistedScreenSettings(expandedGoalsScreenPath(sessionId), expandedGoalsKey);
    }
  }

  public constructGoalsTree(sessionId: string, goals: IGoal[], sort: IGridSortingRule[], matchedGoalIds: string[] = []): IGoalNode {
    const firstLevelParentId = "root";
    const filtersMetaData = this.generateDefaultFiltersMetaData();
    const goalsByParentId: { [parentId: string]: IGoal[] } = {
      root: [],
    };
    const goalsById: IGoalsIdMap = {};
    const expandedGoalIds = getPersistedExpandedGoals(sessionId);
    const goalMetrics = goals.reduce((acc, { metrics }) => acc.concat(metrics || []), []);

    for (const goal of goals) {
      goal.folded = null;
      goal.filtersMatched = 0;
      goal.childrenFiltersMatched = 0;
      goal.subtreeFiltersMatched = 0;
      goal.filterOutChildren = matchedGoalIds.length > 0;

      if (!goal.name) {
        goal.name = "";
      }

      if (matchedGoalIds.includes(goal.id)) {
        goal.filtersMatched += 1;
      }

      // expanding or collapsing the goal only if it is explicitly set
      const goalState = expandedGoalIds[goal.id];
      if (goalState === true) {
        goal.folded = false;
        goal.filterOutChildren = false;
      } else if (goalState === false) {
        goal.folded = true;
      }

      // assign correct parent id to the child goal when parent type is metric
      if (goal.parentType === "metric") {
        const parentMetric = goalMetrics.find((metric) => metric.id === goal.parentId);
        if (parentMetric) {
          goal.parentId = parentMetric.goalId;
        }
      }

      // storing root nodes with empty string as parentId/key
      if (!goal.parentId) {
        goal.parentId = firstLevelParentId;
      }

      goalsByParentId[goal.parentId] = goalsByParentId[goal.parentId] || [];
      goalsByParentId[goal.parentId].push(goal);
      goalsById[goal.id] = goal;
    }

    // gathering all goals which have parents that are not present in this collection
    // this is so that we include them on the root level instead of completely missing them
    // from the resulting hierarchy
    const orphanedGoals: IGoal[] = [];
    for (const goal of goals) {
      if (goal.parentId !== firstLevelParentId && !goalsById[goal.parentId]) {
        goal.orphaned = true;
        orphanedGoals.push(goal);
      }
    }

    goalsByParentId[firstLevelParentId].push(...orphanedGoals);

    // the goals tree can have multiple root nodes so we create a fake root node to start the tree recursion
    const filterOutRootChildren = matchedGoalIds.length > 0 && expandedGoalIds[firstLevelParentId] !== true;
    const fakeRootNode: IGoalNode = {
      depth: 0,
      children: [],
      goal: this.getFakeRootGoalNode(goalsByParentId[firstLevelParentId].length, filterOutRootChildren),
      meta: filtersMetaData,
    };

    this.fillTreeRecursive(firstLevelParentId, fakeRootNode, goalsByParentId, sort, goalsById, filtersMetaData);

    return fakeRootNode;
  }

  private fillTreeRecursive(
    parentId: string,
    currentNode: IGoalNode,
    goalsByParent: { [parentId: string]: IGoal[] },
    sort: IGridSortingRule[],
    goalsById: IGoalsIdMap,
    filtersMetaData: IGoalsAlignmentMeta
  ) {
    this.processUpdateFiltersMetaData(currentNode, filtersMetaData.matchedFiltersData, goalsById);

    const childrenGoals = goalsByParent[parentId];
    if (childrenGoals) {
      // sorting by the specified in the scope order field using angular vs [].sort for better performance
      let sortedChildrenGoals: IGoal[];
      if (sort) {
        sortedChildrenGoals = this.$filter("orderBy")(childrenGoals, sortingRulesToOrderStrings(sort), false, alphanumericComparator);
      } else {
        sortedChildrenGoals = childrenGoals;
      }

      for (const childGoal of sortedChildrenGoals) {
        const childNode: IGoalNode = {
          depth: currentNode.depth + 1,
          children: [],
          goal: childGoal,
        };
        this.fillTreeRecursive(childGoal.id, childNode, goalsByParent, sort, goalsById, filtersMetaData);

        childNode.goal.folding = false;

        const currentNodeGoal = currentNode.goal;
        if (currentNodeGoal) {
          // pushing the matched filters up the tree
          currentNodeGoal.childrenFiltersMatched += childNode.goal.filtersMatched;
          currentNodeGoal.subtreeFiltersMatched += childNode.goal.filtersMatched;

          // pushing the subtree total matched filters count up the tree
          currentNodeGoal.subtreeFiltersMatched += childNode.goal.subtreeFiltersMatched;
        }

        if (currentNodeGoal.subtreeFiltersMatched) {
          this.expandIfNotExplicitlySet(currentNode);
        }

        currentNode.children.push(childNode);
      }
    }
    this.collapseIfNotExplicitlySet(currentNode);
  }

  private getFakeRootGoalNode(childrenCount: number, filterOutChildren: boolean): IGoal {
    const fakeRootGoal = <IGoal>{};
    fakeRootGoal.id = "root";
    fakeRootGoal.folded = false;
    fakeRootGoal.filterOutChildren = filterOutChildren;
    fakeRootGoal.filtersMatched = 0;
    fakeRootGoal.childrenFiltersMatched = 0;
    fakeRootGoal.subtreeFiltersMatched = 0;
    fakeRootGoal.childrenCount = childrenCount;
    return fakeRootGoal;
  }

  private collapseIfNotExplicitlySet(currentNode: IGoalNode) {
    if (currentNode.goal && this.isExpandedStateNotSet(currentNode.goal)) {
      currentNode.goal.folded = true;
    }
  }

  private expandIfNotExplicitlySet(currentNode: IGoalNode) {
    if (currentNode.goal && this.isExpandedStateNotSet(currentNode.goal)) {
      currentNode.goal.folded = false;
    }
  }

  private isExpandedStateNotSet(goal: IGoal) {
    return goal.folded === undefined || goal.folded === null;
  }

  private setPersistedExpandedGoals(sessionId: string, expandedGoals: IdMap<boolean>) {
    setPersistedScreenSettings(expandedGoalsScreenPath(sessionId), expandedGoalsKey, expandedGoals);
  }

  private updateGoals(data: IPartialGoal[], goals: IGoal[]) {
    if (!data) {
      return;
    }

    const newGoalsMap = toIdMap(data);

    goals.forEach((goal) => {
      if (newGoalsMap[goal.id]) {
        const properties = newGoalsMap[goal.id];
        this.updateProperties(goal, properties);
      }
    });
  }

  private updateMetrics(data: IPartialMetric[], goals: IGoal[]) {
    if (!data) {
      return;
    }

    const newMetricsMap = toIdMap(data);

    goals.forEach((goal) => {
      if (goal.metrics) {
        goal.metrics.forEach((metric) => {
          if (newMetricsMap[metric.id]) {
            const properties = newMetricsMap[metric.id];
            this.updateProperties(metric, properties);
          }
        });
      }
    });
  }

  private updateProperties(data: IMetric | IGoal, properties) {
    Object.keys(properties).forEach((property) => {
      if (property === "id" || property === "goalId") {
        return;
      }

      data[property] = properties[property];
      this.updateColoring(data, property);
    });
  }

  private updateColoring(data: IGoal | IMetric, property: string) {
    if (property === "attainment") {
      data.color = this.progressColoring.getColorOrGeneric(data[property]);
    }

    if (property === "fullAggregatedAttainment" && (data as IGoal).aggregatedAttainmentColor) {
      (data as IGoal).aggregatedAttainmentColor = this.progressColoring.getColorOrGeneric(data[property]);
    }
  }

  private generateDefaultFiltersMetaData(): IGoalsAlignmentMeta {
    return {
      matchedFiltersData: {
        ownerIds: [],
        tags: [],
      },
    };
  }

  private processUpdateFiltersMetaData(currentNode: IGoalNode, nodesMatched: IGoalsAlignmentMatchedFiltersData, goalsIdMap: IGoalsIdMap): void {
    const iterationKeys: { goalKey: "assigneeId" | "tags"; filterKey: "ownerIds" | "tags" }[] = [
      { goalKey: "assigneeId", filterKey: "ownerIds" },
      { goalKey: "tags", filterKey: "tags" },
    ];

    iterationKeys.forEach((keys): void => {
      if (currentNode.goal.filtersMatched[keys.goalKey]) {
        if (!nodesMatched[keys.filterKey][currentNode.depth]) {
          nodesMatched[keys.filterKey][currentNode.depth] = [];
        }

        const data: IGoalsAlignmentFilterMeta = {
          rootParentId: "",
          goalId: currentNode.goal.id,
          depth: currentNode.depth,
        };

        let currentGoal: IGoal = currentNode.goal;

        do {
          data.rootParentId = goalsIdMap[currentGoal.parentId] ? goalsIdMap[currentGoal.parentId].id : currentGoal.id;
          currentGoal = goalsIdMap[currentGoal.parentId];
        } while (currentGoal);

        nodesMatched[keys.filterKey][currentNode.depth].push(data);
      }
    });
  }
}
